import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

// Admin Tab Components
import OverviewTab from '../components/admin/OverviewTab';
import UsersTab from '../components/admin/UsersTab';
import ProductsTab from '../components/admin/ProductsTab';
import CoursesTab from '../components/admin/CoursesTab';
import SignalsTab from '../components/admin/SignalsTab';
import OrdersTab from '../components/admin/OrdersTab';
import LiveSessionsTab from '../components/admin/LiveSessionsTab';
import OnDemandTab from '../components/admin/OnDemandTab';

const AdminPanel = () => {
  const { isAdmin } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [signals, setSignals] = useState([]);
  const [orders, setOrders] = useState([]);
  const [liveSessions, setLiveSessions] = useState([]);
  const [onDemandVideos, setOnDemandVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    try {
      const [analyticsRes, usersRes, productsRes, coursesRes, signalsRes, ordersRes, liveSessionsRes, onDemandRes] = await Promise.all([
        api.get('/admin/analytics'),
        api.get('/admin/users'),
        api.get('/products'),
        api.get('/courses'),
        api.get('/signals'),
        api.get('/admin/orders'),
        api.get('/admin/live-sessions'),
        api.get('/admin/on-demand')
      ]);
      setAnalytics(analyticsRes.data);
      setUsers(usersRes.data.users);
      setProducts(productsRes.data);
      setCourses(coursesRes.data);
      setSignals(signalsRes.data);
      setOrders(ordersRes.data.orders);
      setLiveSessions(liveSessionsRes.data.sessions);
      setOnDemandVideos(onDemandRes.data.videos);
    } catch {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // User handlers
  const handleUpdateUserRole = useCallback(async (userId, role) => {
    try {
      await api.put(`/admin/users/${userId}`, { role });
      toast.success('User role updated');
      fetchData();
    } catch {
      toast.error('Failed to update user');
    }
  }, [fetchData]);

  // Product handlers
  const handleCreateProduct = useCallback(async (productData) => {
    try {
      await api.post('/admin/products', productData);
      toast.success('Product created');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create product');
      throw error;
    }
  }, [fetchData]);

  const handleDeleteProduct = useCallback(async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.delete(`/admin/products/${productId}`);
      toast.success('Product deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete product');
    }
  }, [fetchData]);

  // Course handlers
  const handleCreateCourse = useCallback(async (courseData) => {
    try {
      await api.post('/admin/courses', courseData);
      toast.success('Course created');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create course');
      throw error;
    }
  }, [fetchData]);

  const handleDeleteCourse = useCallback(async (courseId) => {
    if (!window.confirm('Delete this course?')) return;
    try {
      await api.delete(`/admin/courses/${courseId}`);
      toast.success('Course deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete course');
    }
  }, [fetchData]);

  // Signal handlers
  const handleCreateSignal = useCallback(async (signalData) => {
    try {
      await api.post('/admin/signals', signalData);
      toast.success('Signal created');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create signal');
      throw error;
    }
  }, [fetchData]);

  const handleDeleteSignal = useCallback(async (signalId) => {
    if (!window.confirm('Delete this signal?')) return;
    try {
      await api.delete(`/admin/signals/${signalId}`);
      toast.success('Signal deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete signal');
    }
  }, [fetchData]);

  // Live Session handlers
  const handleCreateLiveSession = useCallback(async (sessionData) => {
    try {
      await api.post('/admin/live-sessions', sessionData);
      toast.success('Live session scheduled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule session');
      throw error;
    }
  }, [fetchData]);

  const handleUpdateLiveSession = useCallback(async (sessionId, sessionData) => {
    try {
      await api.put(`/admin/live-sessions/${sessionId}`, sessionData);
      toast.success('Live session updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update session');
      throw error;
    }
  }, [fetchData]);

  const handleDeleteLiveSession = useCallback(async (sessionId) => {
    if (!window.confirm('Delete this live session?')) return;
    try {
      await api.delete(`/admin/live-sessions/${sessionId}`);
      toast.success('Live session deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete session');
    }
  }, [fetchData]);

  // On-Demand Video handlers
  const handleCreateOnDemandVideo = useCallback(async (videoData) => {
    try {
      await api.post('/admin/on-demand', videoData);
      toast.success('On-demand video added');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add video');
      throw error;
    }
  }, [fetchData]);

  const handleUpdateOnDemandVideo = useCallback(async (videoId, videoData) => {
    try {
      await api.put(`/admin/on-demand/${videoId}`, videoData);
      toast.success('Video updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update video');
      throw error;
    }
  }, [fetchData]);

  const handleDeleteOnDemandVideo = useCallback(async (videoId) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await api.delete(`/admin/on-demand/${videoId}`);
      toast.success('Video deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete video');
    }
  }, [fetchData]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-red-500">Admin access required</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-outfit text-3xl sm:text-4xl font-light text-white mb-2">
            Admin <span className="text-[#D4AF37]">Panel</span>
          </h1>
          <p className="text-zinc-400">Manage users, content, and products</p>
        </motion.div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#121212] border border-white/5 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              Users
            </TabsTrigger>
            <TabsTrigger value="products" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              Products
            </TabsTrigger>
            <TabsTrigger value="courses" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              Courses
            </TabsTrigger>
            <TabsTrigger value="live-sessions" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              Live Sessions
            </TabsTrigger>
            <TabsTrigger value="on-demand" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              On-Demand
            </TabsTrigger>
            <TabsTrigger value="signals" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              Signals
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              Orders
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab analytics={analytics} />
          </TabsContent>

          <TabsContent value="users">
            <UsersTab users={users} onUpdateUserRole={handleUpdateUserRole} />
          </TabsContent>

          <TabsContent value="products">
            <ProductsTab 
              products={products} 
              onCreateProduct={handleCreateProduct} 
              onDeleteProduct={handleDeleteProduct} 
            />
          </TabsContent>

          <TabsContent value="courses">
            <CoursesTab 
              courses={courses} 
              onCreateCourse={handleCreateCourse} 
              onDeleteCourse={handleDeleteCourse}
              onRefresh={fetchData}
            />
          </TabsContent>

          <TabsContent value="live-sessions">
            <LiveSessionsTab 
              sessions={liveSessions}
              courses={courses}
              onCreateSession={handleCreateLiveSession}
              onUpdateSession={handleUpdateLiveSession}
              onDeleteSession={handleDeleteLiveSession}
            />
          </TabsContent>

          <TabsContent value="on-demand">
            <OnDemandTab 
              videos={onDemandVideos}
              courses={courses}
              onCreateVideo={handleCreateOnDemandVideo}
              onUpdateVideo={handleUpdateOnDemandVideo}
              onDeleteVideo={handleDeleteOnDemandVideo}
            />
          </TabsContent>

          <TabsContent value="signals">
            <SignalsTab 
              signals={signals} 
              onCreateSignal={handleCreateSignal} 
              onDeleteSignal={handleDeleteSignal} 
            />
          </TabsContent>

          <TabsContent value="orders">
            <OrdersTab orders={orders} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
